import { type FC, type IframeHTMLAttributes, useRef } from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Card,
  Center,
  Dialog,
  formatDateTimeFromISO,
  LoadingSpinner,
  MarkdownPreview,
  TagIntent,
  TooltipIntent,
  TriggerButton,
} from "widgets";
import { type ControlledDialog, useControlledDialog, useVisuallyLoading } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import { capitalizeFirstLetter } from "@/app-utils/string.ts";
import { selMessage } from "@/messages/message.ts";
import { urls } from "@/modules/app/utils/urls.ts";
import { FileDownloadButton } from "@/modules/files/components/FileDownloadButton";
import { FilePreviewTextField } from "@/modules/files/components/FilePreviewTextField";
import { HeaderIcon } from "@/modules/files/components/HeaderIcon";
import { PDFPreview, type PdfPreviewActionsApi } from "@/modules/files/components/PDFPreview";
import { usePreviewDialogBasicsQuery } from "@/modules/files/graphql/files-queries.gql";
import style from "@/modules/files/styles/FilePreviewDialog.module.scss";
import { getFilePreview } from "@/modules/files/utils/fetch-preview.ts";
import type { IAppState } from "@/redux/reducer.ts";
import { HTML_FILE_PREVIEW } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

interface FilePreviewDialogProps extends ControlledDialog {
  fileId: number;
}

export const FilePreviewControlledDialog: FC<FilePreviewDialogProps> = (props) => {
  const pdfPreviewActionsApiRef = useRef<PdfPreviewActionsApi>(null);
  const { dialogProps, underlayProps, dialogRef } = useControlledDialog(props.onCancel);
  const [previewType, setPreviewType] = useState<{ contentType: string; charset: string }>();
  const [previewContent, setPreviewContent] = useState<string>();
  const htmlFilePreviewMode = useSelector<IAppState, HTML_FILE_PREVIEW | null>(
    (state) => state.config.configuration.application?.htmlFilePreview as HTML_FILE_PREVIEW | null,
  );
  // define the iframe security for html file preview based on the configured setting
  const htmlPreviewSecurityOptions: IframeHTMLAttributes<HTMLIFrameElement> = {};
  if (htmlFilePreviewMode === HTML_FILE_PREVIEW.STRICT) {
    htmlPreviewSecurityOptions.sandbox = "allow-scripts";
  }

  // load the data
  const {
    data,
    loading: isLoadingDialog,
    error,
  } = usePreviewDialogBasicsQuery({
    variables: { fileId: props.fileId },
  });
  void useError(error);

  const [isLoadingFile, setLoadingFile] = useState<boolean>(true);
  const isLoading = useVisuallyLoading(isLoadingDialog || isLoadingFile, 300);
  const file = data?.projectFileById;

  const [fetchError, setFetchError] = useState<string>();

  useEffect(() => {
    void getFilePreview(props.fileId).then((res) => {
      if (res.error) {
        setFetchError(res.error);
        return;
      }

      if (res.type == null || res.content == null) {
        setFetchError("An error occured");
        return;
      }

      const splitTypeAndCharset = res.type.split(";");
      const contentType = splitTypeAndCharset[0].trim();
      const charset = splitTypeAndCharset[1]?.trim(); // does not necessarily exist
      setPreviewType({ contentType, charset });

      // we want to get the plain text only in this specific case
      if (contentType === "text/plain" || contentType === "text/markdown") {
        setPreviewContent(res.content as string);
      } else {
        const reader = new FileReader();
        reader.readAsArrayBuffer(res.content as Blob);
        reader.onload = (e) => {
          const buffer = e.target?.result;

          if (buffer) {
            const previewFile = new File([buffer], file?.fileName ?? "", {
              type: contentType,
            });

            setPreviewContent(URL.createObjectURL(previewFile));
          }
        };
      }
      setLoadingFile(false);
    });
  }, []);

  return (
    <Dialog
      ref={dialogRef}
      title={file?.fileName ?? ""}
      subtitle={
        file?.lastModified ? `Modified on ${formatDateTimeFromISO(file?.lastModified, { hideTime: true })}` : undefined
      }
      icon={file ? <HeaderIcon file={file} /> : undefined}
      closeFn={() => {
        props.onCancel?.();
      }}
      domProps={dialogProps}
      underlayProps={underlayProps}
      fullscreen
      actions={[
        previewType?.contentType === "application/pdf" && (
          <>
            <TriggerButton
              showLabel={true}
              icon="add_circle"
              label="Zoom In"
              onPress={() => pdfPreviewActionsApiRef.current?.zoomIn()}
            />
            <TriggerButton
              showLabel={true}
              icon="do_not_disturb_on"
              label="Zoom Out"
              onPress={() => pdfPreviewActionsApiRef.current?.zoomOut()}
            />
          </>
        ),
        file ? <FileDownloadButton file={file} key={`preview-dialog-download-button-${props.fileId}`} /> : null,
      ]}
      contentStyle={{ padding: 0, display: "flex", width: "100%", height: "100%", overflow: "hidden" }}
      tag={
        previewType?.contentType === "text/html" && htmlFilePreviewMode === HTML_FILE_PREVIEW.STRICT
          ? {
              label: htmlFilePreviewMode,
              intentColor: TagIntent.Warning,
              tooltip: {
                intent: TooltipIntent.Warning,
                title: selMessage({
                  id: "config.application.htmlPreview.strict.title",
                  values: { mode: capitalizeFirstLetter(htmlFilePreviewMode) },
                }),
                children: selMessage({ id: "config.application.htmlPreview.strict.message" }),
              },
            }
          : undefined
      }
    >
      {/* do not include !preview within isVisuallyLoading because it is too fast and the result will never be true*/}
      {!fetchError && (isLoading || previewContent == undefined || !previewType) ? (
        <LoadingSpinner inCenter />
      ) : fetchError ? (
        <ContentPlaceholder>{fetchError}</ContentPlaceholder>
      ) : (
        previewContent &&
        // most specific types first
        (previewType?.contentType === "text/html" ? (
          <div className={style.iframeContainer}>
            <iframe
              key={`html-preview`}
              title={file?.fileName ?? ""}
              src={previewContent}
              className={style.iFrameElement}
              itemType={previewType.contentType}
              draggable={false}
              // When the embedded document has the same origin as the embedding page,
              // it is strongly discouraged to use both allow-scripts and allow-same-origin,
              // as that lets the embedded document remove the sandbox attribute —
              // making it no more secure than not using the sandbox attribute at all.
              // @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe
              //! With `allow-same-origin` you have access to the localStorage and so any users token
              //! (If you ask them to open the preview with a malicious script)
              //! => Same for opening the file in another tab. Disallow that! Then all security are off.
              {...htmlPreviewSecurityOptions}
            />
          </div>
        ) : previewType?.contentType === "text/plain" ? (
          <FilePreviewTextField key={`text-preview`} value={previewContent} style={{ borderWidth: "1px 0 0" }} />
        ) : previewType?.contentType === "text/markdown" ? (
          <Card noBorder scrollable>
            <MarkdownPreview markdownText={previewContent} baseUrl={urls.basePath} />
          </Card>
        ) : previewType?.contentType === "application/pdf" ? (
          <PDFPreview pdfUrl={previewContent} ref={pdfPreviewActionsApiRef} />
        ) : // images have specific types, but all of them are rendered the same way
        previewType?.contentType.startsWith("image/") ? (
          <Center className={style.imagePreviewContainer}>
            <img
              key={`image-preview`}
              src={previewContent}
              className={style.imagePreview}
              alt={`image preview`}
              draggable={false}
            />
          </Center>
        ) : (
          // if we cannot map the type to a specific renderer
          <ContentPlaceholder>
            {selMessage({ id: "file.preview.unsupported", values: { fileType: previewType?.contentType } })}
          </ContentPlaceholder>
        ))
      )}
    </Dialog>
  );
};
